import React from 'react';
import { useTranslation } from 'react-i18next';
import TranslationNamespaces from '../../locales/TranslationNamespaces';
import { QInputForm, QSpinner } from '@kubiq-base/core';
import { useForm } from 'react-hook-form';
import { useApi } from '../../state/ApiContext';
import { useCache } from '../../state/CachingContext';
import PageError from '../Shared/PageError';
import FlexFormContainer from '../Shared/FlexContainers/FlexFormContainer';
import CenteredQButton from '../Shared/CenteredQButton';
import HeaderWithDescription from '../Shared/HeaderWithDescription';
import FlexContainer from '../Shared/FlexContainers/FlexContainer';

const Request2FAReset = (): JSX.Element => {
  const namespace = TranslationNamespaces.recovery;
  const { t } = useTranslation(namespace);
  const { postRequest } = useApi();
  const { getCache, clearCache } = useCache();
  const session = getCache();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<{ smsCode: string }>();

  const [isVerifyingEmail, setIsVerifyingEmail] = React.useState(true);
  const [showErrorMessage, setShowErrorMessage] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [resetComplete, setResetComplete] = React.useState(false);
  const [codeError, setCodeError] = React.useState<string | undefined>();

  const onSubmit = async (data: { smsCode: string }): Promise<void> => {
    setIsSubmitting(true);
    setCodeError(undefined);

    postRequest(
      'TwoFactorAuthentication/VerifyResetAuthenticator',
      {
        pincode: data.smsCode,
        identifier: session.email,
      },
      undefined,
      ['invalidCode'],
    )
      .then(() => {
        setResetComplete(true);
        clearCache('token');
      })
      .catch((err) => {
        setIsSubmitting(false);
        if (err.response?.data.errors.global[0] === 'invalidCode') setCodeError('invalidCode');
        else setShowErrorMessage(true);
      });
  };

  const bootstrapAsync = async (): Promise<void> => {
    if (session.email && session.token) {
      await postRequest('TwoFactorAuthentication/VerifyEmailCode', {
        pincode: session.token,
        identifier: session.email,
      })
        .then(() => {
          setIsVerifyingEmail(false);
          postRequest('Sms/SendVerificationSms', {}); // Needs to be ratelimited in BE
        })
        .catch(() => setShowErrorMessage(true));
    } else {
      setShowErrorMessage(true);
    }
  };

  React.useEffect(() => {
    bootstrapAsync();
  }, []);

  return (
    <>
      {showErrorMessage ? (
        <PageError message='wentWrong' translationNamespace={TranslationNamespaces.global} />
      ) : isVerifyingEmail ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 72 }}>
          <QSpinner />
        </div>
      ) : !resetComplete ? (
        <>
          <HeaderWithDescription translationNamespace={namespace} headerSuffix='SMS' description='descriptionSMS2FA' />
          <FlexFormContainer onSubmit={handleSubmit(onSubmit)}>
            <QInputForm
              control={control}
              name='smsCode'
              label={t('smsCode')}
              autoFocus
              rules={{ required: 'requiredField' }}
              isError={(errors.smsCode && t(errors.smsCode.message as string)) || (codeError && t(codeError as string))}
            />

            <CenteredQButton type='submit' isLoading={isSubmitting} fullWidth color='secondary'>
              {t('next')}
            </CenteredQButton>
          </FlexFormContainer>
        </>
      ) : (
        <>
          <HeaderWithDescription
            translationNamespace={namespace}
            headerSuffix='Complete'
            description='description2FAComplete'
          />
          <FlexContainer>
            <CenteredQButton to='/Enable2FA' fullWidth color='secondary'>
              {t('continue')}
            </CenteredQButton>
          </FlexContainer>
        </>
      )}
    </>
  );
};

export default Request2FAReset;
